import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin } from 'lucide-react';

export default function WhatIsLikeKind() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2" style={{ color: 'var(--fy-text-secondary)' }} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }} data-testid="page-title">
          What is LikeKind
        </h1>
        <p className="mb-10" style={{ color: 'var(--fy-text-disabled)' }}>A different kind of platform</p>

        <div className="space-y-10">

          {/* Lead with what it does */}
          <section className="p-6 rounded-xl" style={{ backgroundColor: 'var(--lk-sage-light)', border: '1px solid var(--lk-sage)' }}>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-primary)' }}>
              LikeKind helps people discover, create, and join real-world activities near them.
            </p>
            <p className="mt-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              It's built around coordination — helping people organize things and show up — rather than content, feeds, or social performance.
            </p>
          </section>

          {/* How it works */}
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <MapPin className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              How It Works
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { step: '1', title: 'Discover', desc: 'See what\'s happening nearby on the map' },
                { step: '2', title: 'Create', desc: 'Start something in seconds' },
                { step: '3', title: 'Join', desc: 'Participate with real people' },
                { step: '4', title: 'Coordinate', desc: 'Stay aligned through simple signals' },
              ].map(item => (
                <div key={item.step} className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'var(--lk-sage)' }}>
                    <span className="text-lg font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="font-semibold mb-1" style={{ color: 'var(--fy-text-primary)' }}>{item.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Built for */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Built For
            </h2>
            <ul className="space-y-3">
              {[
                'People looking for things to do nearby',
                'Families who want a calm, structured digital space',
                'Schools and organizations coordinating participation',
                'Anyone who wants to connect without performing',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
                  <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Design approach */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Design Approach
            </h2>
            <div className="space-y-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Calm by default</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  The system is designed so you can check in, see what's happening, and leave whenever you want.
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Participation over performance</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  What matters is showing up — not how visible you are or how much you post.
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Activities are shown by time and location</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Nothing is ranked by engagement or popularity. You see what's near you and what's happening soon.
                </p>
              </div>
            </div>
          </section>

          {/* Closing */}
          <section className="p-6 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
            <p className="text-lg leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              LikeKind is a place to coordinate and show up if you want to.
            </p>
            <p className="mt-2 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              That's the whole idea.
            </p>
          </section>

          {/* Related */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <div className="flex flex-wrap gap-3">
              {[
                { to: '/faq', label: 'FAQ' },
                { to: '/trust', label: 'Trust & Safety' },
                { to: '/founder-statement', label: "Founder's Letter" },
              ].map(link => (
                <Link key={link.to} to={link.to} className="px-4 py-2 rounded-full text-sm" style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}>
                  {link.label}
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <footer className="mt-16 py-8" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p style={{ color: 'var(--fy-text-disabled)' }}>© {new Date().getFullYear()} LikeKind Inc.</p>
        </div>
      </footer>
    </div>
  );
}
